import dayjs from "dayjs";
import type { EventDateOption } from "../_types/wizard-types";

/** A single ranked date option read as a plain `{ startDate, endDate }` range. */
export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Reads a range-shaped `EventDateOption` as `{ startDate, endDate }`, or `null` for the single-day-list
 * shape (not used by the Dates step's picker) or an unset/empty option (so a blank `{ startDate: "",
 * endDate: "" }` renders as an empty picker instead of `dayjs("")` → "Invalid Date").
 */
export function asRange(option: EventDateOption | null): DateRange | null {
  if (option && "startDate" in option && option.startDate && option.endDate) return option;
  return null;
}

/** Inclusive length of a date range, in days (same-day range is 0). */
export function rangeSpanDays(range: DateRange): number {
  return dayjs(range.endDate).diff(dayjs(range.startDate), "day");
}

/** Whether every range in `ranges` covers the same number of nights. Vacuously true for 0 or 1 ranges. */
export function allSpansMatch(ranges: DateRange[]): boolean {
  if (ranges.length <= 1) return true;
  const first = rangeSpanDays(ranges[0]);
  return ranges.every((r) => rangeSpanDays(r) === first);
}

/** Whether any two ranges share the exact same start/end dates — a ranked list of alternates that's
 *  actually the same dates picked twice isn't a meaningful alternate. */
export function hasDuplicateRange(ranges: DateRange[]): boolean {
  const seen = new Set<string>();
  for (const r of ranges) {
    const key = `${r.startDate}_${r.endDate}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}
